import React, { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircle, faEllipsisVertical, faPlus, faMinus } from "@fortawesome/pro-light-svg-icons";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { capitalize, getCreditCardIcon, handleChangeEventInput } from "helpers/Helpers";
import { INewReservationState } from "pages/secure/facility/accommodation/reservations/types";
import GuestPicker, { IRoom } from "components/guestPicker/GuestPicker";
import { IAccommodationCategory } from "pages/secure/facility/accommodation/categories/Categories";
import { IAccommodationUnitType } from "pages/secure/facility/accommodation/unitTypes/UnitTypes";
import { ICustomer } from "redux/reducers/models/customer";
import FormLayout from "components/form/FormLayout";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import SelectCustomer from "elements/register/SelectCustomer";
import Avatar from "components/avatar/Avatar";
import MultipleActionDropdown from "components/multipleActionDropdown/MultipleActionDropdown";
import CardSection from "components/cardSection/CardSection";
import { ButtonNew as Button } from "components/buttonNew";
import Input from "components/form/input/Input";
import Checkbox from "components/form/checkbox/Checkbox";
import { Select } from "components/select/index";
import Sheet from "components/sheet/Sheet";
import "./accommodationReservationModal.scss";
import { faCalendarMinus } from "@fortawesome/pro-regular-svg-icons";

interface IBillingState {
  address: string;
  country: number;
  city: string;
  province: number;
  postalCode: string;
}

interface IAccommodationReservationModalProps {
  newReservationState: INewReservationState;
  setNewReservationState: React.Dispatch<React.SetStateAction<INewReservationState>>;
  billingState: IBillingState;
  setBillingState: React.Dispatch<React.SetStateAction<IBillingState>>;
  rooms: IRoom[];
  setRooms: (rooms: IRoom[]) => void;
  currentStep: number;
  setCurrentStep: React.Dispatch<React.SetStateAction<number>>;
  handleContinue: () => void;
  handleBack: () => void;
  steps: { id: number; title: string; description: string }[];
  accommodationCategories: IAccommodationCategory[];
  mergedUnitTypes: (IAccommodationUnitType & { price: number; isSoldOut?: boolean; nextAvailableDate?: string })[];
  customerPaymentMethods: any[];
  selectedUnitTypes: IAccommodationUnitType[];

  // Handlers from Parent
  onClose: () => void;
  handleBookReservation: () => void;
  handleCustomerSelection: (customer: ICustomer) => void;
  handleCardInputChange: (event: any) => void;
  handleCheckboxChange: (type: IAccommodationUnitType) => void;
}

const canadianProvinces = [
  { id: 1, name: "Alberta" },
  { id: 2, name: "British Columbia" },
  { id: 3, name: "Manitoba" },
  { id: 4, name: "New Brunswick" },
  { id: 5, name: "Newfoundland and Labrador" },
  { id: 6, name: "Northwest Territories" },
  { id: 7, name: "Nova Scotia" },
  { id: 8, name: "Nunavut" },
  { id: 9, name: "Ontario" },
  { id: 10, name: "Prince Edward Island" },
  { id: 11, name: "Quebec" },
  { id: 12, name: "Saskatchewan" },
  { id: 13, name: "Yukon" },
];

const isValidPostalCode = (code: string) => /^[A-Za-z]\d[A-Za-z][ -]?\d[A-Za-z]\d$/.test(code);

export const AccommodationReservationModal: React.FC<IAccommodationReservationModalProps> = ({
  newReservationState,
  setNewReservationState,
  billingState,
  setBillingState,
  rooms,
  setRooms,
  currentStep,
  setCurrentStep,
  handleContinue,
  handleBack,
  steps,
  accommodationCategories,
  mergedUnitTypes,
  customerPaymentMethods,
  selectedUnitTypes,
  onClose,
  handleBookReservation,
  handleCustomerSelection,
  handleCardInputChange,
  handleCheckboxChange,
}) => {
  const { t } = useTranslation();

  // Internal Navigation & Filtering State
  const [activeTab, setActiveTab] = useState<{ id: number | null; title: string }>({
    id: null,
    title: t("All"),
  });

  const filteredUnitTypes = useMemo(() => {
    const totalGuests = rooms?.reduce((acc, room) => acc + room.adults + room.children, 0);
    const filtered = activeTab.id
      ? mergedUnitTypes?.filter(unit => unit?.category_id === activeTab.id)
      : mergedUnitTypes;

    return filtered?.filter(unit => totalGuests <= unit.max_occupancy && totalGuests >= unit.base_occupancy);
  }, [mergedUnitTypes, activeTab, rooms]);

  // --- Step Validation Logic ---
  const isStepValid = () => {
    switch (currentStep) {
      case 1:
        return !!newReservationState.arrival_date && !!newReservationState.departure_date && rooms.length > 0;
      case 2:
        return selectedUnitTypes.length > 0;
      case 3:
        return !!newReservationState.customer_id;
      case 4: {
        const hasBillingAddress =
          !!billingState.address &&
          !!billingState.city &&
          !!billingState.country &&
          !!billingState.province &&
          isValidPostalCode(billingState.postalCode);
        if (!newReservationState.cart_token) {
          return false;
        }
        if (newReservationState.newCreditCardActive) {
          return !!newReservationState.newCreditCardCustomerName && hasBillingAddress;
        }
        return !!newReservationState.selectedPaymentMethod && hasBillingAddress;
      }
      default:
        return false;
    }
  };

  // --- Handlers ---
  const handleDateChange = (key: "arrival_date" | "departure_date", selectedDate: Date) => {
    setNewReservationState(prev => ({
      ...prev,
      [key]: selectedDate,
      nightly_rates: [],
      subtotalPrice: null,
      totalPrice: null,
      taxAmount: null,
      cart_token: undefined,
    }));
  };

  const handleSetActiveTab = (id: number | null, title: string) => {
    setActiveTab({ id, title });
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div>
            <p className="step-content_title">{steps[0].title}</p>
            <FormLayout>
              <FormLayout.Group>
                <DatePickerInput
                  months={1}
                  position="left"
                  startingDate={newReservationState.arrival_date}
                  setStartingDate={(date: Date) => handleDateChange("arrival_date", date)}
                  label="Check-in"
                />
                <DatePickerInput
                  months={1}
                  position="right"
                  startingDate={newReservationState.departure_date}
                  setStartingDate={(date: Date) => handleDateChange("departure_date", date)}
                  label="Check-out"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <GuestPicker rooms={rooms} onRoomsChange={setRooms} />
              </FormLayout.Group>
            </FormLayout>
          </div>
        );
      case 2:
        return (
          <div style={{ maxHeight: "calc(90vh - 220px)", overflowY: "auto" }}>
            <p className="step-content_title">{t("Available units:")}</p>
            <div className="category-tabs-container">
              <div
                className={classNames("category-tabs-container_tab", { active: activeTab.id === null })}
                onClick={() => handleSetActiveTab(null, t("All"))}
              >
                <p>{t("All")}</p>
              </div>
              {accommodationCategories?.map(category => (
                <div
                  key={category.id}
                  className={classNames("category-tabs-container_tab", { active: activeTab.id === category.id })}
                  onClick={() => handleSetActiveTab(category.id, category.title)}
                >
                  <p>{category.title}</p>
                </div>
              ))}
            </div>
            <div>
              {filteredUnitTypes?.map((type, index) => {
                const isSelected = selectedUnitTypes.some(u => u.id === type.id);
                const isDisabled = selectedUnitTypes.length > 0 && !isSelected && !type.isSoldOut;
                return (
                  <div
                    key={index}
                    className={classNames("unit-card", {
                      "unit-card_selected": isSelected,
                      "unit-card_disabled": isDisabled,
                      "unit-card_sold-out": type.isSoldOut,
                    })}
                    onClick={() => !isDisabled && !type.isSoldOut && handleCheckboxChange(type)}
                  >
                    <div className="unit-card_room_information">
                      <Checkbox
                        id={`unit_type_${type.id}`}
                        size="medium"
                        checked={isSelected}
                        onChange={() => !isDisabled && !type.isSoldOut && handleCheckboxChange(type)}
                        circle
                        disabled={isDisabled}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p className={classNames("unit-card_title", { "unit-card_title_sold-out": type.isSoldOut })}>
                            {type?.title}
                          </p>
                        </div>
                        <p
                          className={classNames("unit-card_description", {
                            "unit-card_description_sold-out": type.isSoldOut,
                          })}
                        >
                          {t("Sleeps")} {type?.base_occupancy} - {type?.max_occupancy}
                        </p>
                        {/* {type.isSoldOut && type.nextAvailableDate && (
                          <p className="unit-card_availability-info">
                            {t("Next available:")} {type.nextAvailableDate}
                          </p>
                        )} */}
                      </div>
                    </div>
                    <div className="unit-card_price_information">
                      {type.isSoldOut ? (
                        <p className="unit-card_price_sold-out">
                          <FontAwesomeIcon icon={faCalendarMinus} className="unit-card_price_sold-out_icon" />{" "}
                          {t("Sold out")}
                        </p>
                      ) : (
                        <>
                          <p className="unit-card_price">${type?.price.toFixed(2)}</p>
                          <p className="unit-card_price_unit">{t("per night")}</p>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        );
      case 3:
        return (
          <div>
            <p className="step-content_title">{t("Guest information")}</p>
            <SelectCustomer
              onCustomerChange={customer => {
                void handleCustomerSelection(customer);
              }}
              permissionLevel={"facility"}
              clearOnSelection
              usePortal
              autoFocus={!newReservationState?.customer_id}
            />
            {newReservationState?.customer && (
              <div className="reservations-customer-container">
                <div className="flex items-center gap-5">
                  <Avatar size="xlarge" source={newReservationState.customer.profile_image_source} />
                  <div>
                    <p className="reservations-customer-container_title">{newReservationState.customer.full_name}</p>
                    <p className="reservations-customer-container_subtitle">{newReservationState.customer.email}</p>
                    <p className="reservations-customer-container_subtitle">{newReservationState.customer.phone}</p>
                  </div>
                </div>
                <MultipleActionDropdown
                  alignment="right"
                  options={[
                    {
                      type: "handler",
                      label: t("Remove guest"),
                      handler: () =>
                        setNewReservationState(prevState => ({ ...prevState, customer: null, customer_id: null })),
                      icon: "user-slash",
                    },
                  ]}
                  disabled={false}
                >
                  <Button size="xsmall" type="secondary">
                    <FontAwesomeIcon icon={faEllipsisVertical} className="cursor-pointer" />
                  </Button>
                </MultipleActionDropdown>
              </div>
            )}
          </div>
        );
      case 4:
        return (
          <div>
            <p className="step-content_title">{t("Payment details")}</p>
            {newReservationState?.newCreditCardActive ? (
              <div className="flex flex-col gap-4">
                <Input
                  id="newCreditCardCustomerName"
                  value={newReservationState.newCreditCardCustomerName}
                  label={t("Cardholder name")}
                  onChange={e => handleChangeEventInput(e, newReservationState, setNewReservationState)}
                  placeholder={t("Enter full name of cardholder")}
                />
                <CardSection type="one-row" onChange={handleCardInputChange} />
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                {customerPaymentMethods?.map(method => (
                  <div
                    key={method.id}
                    className={classNames("reservations-customer-payment-method-container", {
                      "reservations-customer-payment-method-container-selected":
                        newReservationState?.selectedPaymentMethod?.id === method?.id,
                    })}
                    onClick={() =>
                      setNewReservationState(prev => ({
                        ...prev,
                        selectedPaymentMethod: prev.selectedPaymentMethod?.id === method?.id ? null : method,
                      }))
                    }
                  >
                    <FontAwesomeIcon icon={getCreditCardIcon(String(method.brand))} size="2x" />
                    <div>
                      <p>
                        {capitalize(String(method.brand))} ending in {String(method.last4)}
                      </p>
                      <p className="text-xs opacity-60">Expiry {String(method.expiry)}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}
            <Button
              type="text"
              className="mt-4"
              onClick={() =>
                setNewReservationState(prev => ({ ...prev, newCreditCardActive: !prev.newCreditCardActive }))
              }
            >
              <FontAwesomeIcon icon={newReservationState?.newCreditCardActive ? faMinus : faPlus} />
              <span className="ml-2">
                {newReservationState?.newCreditCardActive ? t("Select credit card") : t("Add new payment method")}
              </span>
            </Button>

            <div className="mt-4">
              <p className="step-content_title">{t("Billing address")}</p>
              <FormLayout>
                <FormLayout.Group>
                  <Input
                    id="address"
                    value={billingState.address}
                    label={t("Address")}
                    onChange={e => handleChangeEventInput(e, billingState, setBillingState)}
                    placeholder={t("Address line 1")}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Select
                    label={t("Country")}
                    onChange={(val: number) => setBillingState(p => ({ ...p, country: val }))}
                    defaultValue={billingState.country}
                    placeholder={t("Select country")}
                    usePortal
                  >
                    <Select.Option value={1} name="Canada">
                      Canada
                    </Select.Option>
                  </Select>
                  <Input
                    id="city"
                    label={t("City")}
                    value={billingState.city}
                    onChange={e => handleChangeEventInput(e, billingState, setBillingState)}
                    placeholder={t("Enter city")}
                  />
                </FormLayout.Group>
                <FormLayout.Group>
                  <Select
                    label={t("Province")}
                    onChange={(val: number) => setBillingState(p => ({ ...p, province: val }))}
                    defaultValue={billingState.province}
                    placeholder={t("Select province")}
                    usePortal
                  >
                    {canadianProvinces.map(p => (
                      <Select.Option key={p.id} value={p.id} name={p.name}>
                        {p.name}
                      </Select.Option>
                    ))}
                  </Select>
                  <Input
                    id="postalCode"
                    label={t("Postal Code")}
                    value={billingState.postalCode}
                    onChange={e => handleChangeEventInput(e, billingState, setBillingState)}
                    error={billingState.postalCode !== "" && !isValidPostalCode(billingState.postalCode)}
                    placeholder={t("Enter postal code")}
                  />
                </FormLayout.Group>
              </FormLayout>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <Sheet
      open={newReservationState?.openNewReservation}
      title={t("Create a reservation")}
      size="medium"
      onCancel={currentStep === 1 ? onClose : handleBack}
      onClose={onClose}
      cancelText={currentStep === 1 ? t("Cancel") : t("Go back")}
      okText={currentStep === steps.length ? t("Confirm and pay") : t("Continue")}
      onOk={currentStep === steps.length ? handleBookReservation : handleContinue}
      okDisabled={!isStepValid()}
      closable
    >
      <div className="reservation-modal">
        <div className="property-modal-content">
          <div className="left-panel">
            <ul className="steps">
              {steps.map(step => (
                <li
                  key={step.id}
                  className={classNames("step cursor-pointer", {
                    active: step.id === currentStep,
                    completed: step.id < currentStep,
                    inactive: step.id > currentStep,
                  })}
                  onClick={() => {
                    if (step.id < currentStep) {
                      setCurrentStep(step.id);
                    }
                  }}
                >
                  <div>
                    <div className="circle">
                      {step.id < currentStep ? (
                        <svg xmlns="http://www.w3.org/2000/svg" width="30" height="30" viewBox="0 0 24 24" fill="none">
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M17.0965 7.39004L9.9365 14.3L8.0365 12.27C7.6865 11.94 7.1365 11.92 6.7365 12.2C6.3465 12.49 6.2365 13 6.4765 13.41L8.7265 17.07C8.9465 17.41 9.3265 17.62 9.7565 17.62C10.1665 17.62 10.5565 17.41 10.7765 17.07C11.1365 16.6 18.0065 8.41004 18.0065 8.41004C18.9065 7.49004 17.8165 6.68004 17.0965 7.38004V7.39004Z"
                            fill="white"
                          />
                        </svg>
                      ) : (
                        <FontAwesomeIcon className="step-icon" icon={faCircle} />
                      )}
                    </div>

                    <div className="line"></div>
                  </div>

                  <div className="text">
                    <div className="title">{step.title}</div>
                    <div className="desc">{step.description}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          <div className="right-panel">
            <div className="step-content">{renderStepContent()}</div>
          </div>
        </div>
      </div>
    </Sheet>
  );
};
